import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { AppErrors, NotFoundError } from '@fitness-force/models';;
import type { AccessRequest, CREATE_STAFF, ReportFilterScheduleResponse, UPDATE_STAFF } from '@fitness-force/models';
import { environment } from '@fitness-force/environments';
import type { STAFF_MODEL } from '@fitness-force/models';
import { catchError, map, Observable, throwError } from 'rxjs';

type StaffListQueryParam = { name: string; value: unknown };

@Injectable({
  providedIn: 'root'
})
export class StaffService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  GetStaffList(BASE_URL: string, TENANT_ID: number, allParams: StaffListQueryParam[] = []): Observable<any> {
    let parsedParams = new HttpParams();
    if (TENANT_ID != 0)  parsedParams = parsedParams.append("stf_tent_access_id", TENANT_ID);

    parsedParams = this.appendQueryParams(parsedParams, allParams);
    return this.http.get(BASE_URL + CONST_URL_SEGEMENTS.STAFF.GET_STAFF_LIST, {
      headers: this.httpOptions.headers, params: parsedParams
    }).pipe(map((res: any) => {
      if (res) {
        return res;
      }
      else {
        return null;
      }
    }), catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      }
      return throwError(() => new AppErrors(error));
    }));
  }

  GetStaffById(STAFF_ID: number): Observable<STAFF_MODEL> {
    let url = environment.BASE_URL + CONST_URL_SEGEMENTS.STAFF.GET_STAFF_LIST + '/' + STAFF_ID;
    return this.http.get(url, { headers: this.httpOptions.headers }).pipe(map((res: any) => {
      if (res) { return res; } else { return null; }
    }), catchError((error: Response) => {
      if (error.status === 404)
        return throwError(() => new NotFoundError(error));

      return throwError(() => new AppErrors(error));
    }));
  }

  ChangePassword(BASE_URL: string, STAFF_ID: number,NEW_PASSWORD: string): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_CHANGE_PASSWORD.replace('{staff_id}', STAFF_ID.toString());
    const params = {'new_password': NEW_PASSWORD};
    return this.http.post(url, params, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => res),
      catchError((error: Response) => {
        if (error.status === 404) return throwError(() => new NotFoundError(error));
        return throwError(() => new AppErrors(error));
      })
    );
  }

  changeSelfPassword(newPassword: string): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_CHANGE_PASSWORD_ME;
    const params = {'password': newPassword};
    return this.http.post(url, params, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => res),
      catchError((error: Response) => {
        if (error.status === 404) return throwError(() => new NotFoundError(error));
        return throwError(() => new AppErrors(error));
      })
    );
  }

  SuspendStaff(BASE_URL: string, STAFF_ID: number): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_SUSPEND.replace('{staff_id}', STAFF_ID.toString());
    return this.http.post(url, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => res),
      catchError((error: Response) => {
        if (error.status === 404) return throwError(() => new NotFoundError(error));
        return throwError(() => new AppErrors(error));
      })
    );
  }

  ActivateStaff(BASE_URL: string, STAFF_ID: number): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_ACTIVE.replace('{staff_id}', STAFF_ID.toString());
    return this.http.post(url, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => res),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  TerminateStaff(BASE_URL: string, STAFF_ID: number): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_TERMINATE.replace('{staff_id}', STAFF_ID.toString());
    return this.http.post(url, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => res),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  ChangeOwnerStaff(BASE_URL: string, STAFF_ID: number, NEW_OWNER_STAFF_ID: number): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_CHANGEOWNER.replace('{staff_id}', STAFF_ID.toString());
    const params = { 'new_owner_staff_id': NEW_OWNER_STAFF_ID };
    return this.http.post(url, params, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => res),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  ChangeReportingManager(BASE_URL: string, STAFF_ID: number, NEW_REPORTING_MANGER: number): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_CHANGEREPORTINGMANAGER.replace('{staff_id}', STAFF_ID.toString());
    const params = { 'reports_to_id': NEW_REPORTING_MANGER };
    return this.http.post(url, params, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => res),
      catchError((error: Response) => {
        if (error.status === 404) return throwError(() => new NotFoundError(error));
        return throwError(() => new AppErrors(error));
      })
    );
  }

  ChangeTenantAccess(BASE_URL: string, STAFF_ID: number, NEW_TENANT_ACCESS: number[]): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_CHANGECENTERACCESS.replace('{staff_id}', STAFF_ID.toString());
    const params = { 'tenant_access_id': NEW_TENANT_ACCESS };
    return this.http.post(url, params, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => { return res; }),
      catchError((error: Response) => {
        if (error.status === 404) return throwError(() => new NotFoundError(error));
        return throwError(() => new AppErrors(error));
      })
    );
  }

  GetStaffReportSchedule(staffId: number): Observable<ReportFilterScheduleResponse[]> {
    return this.http.get(`${environment.BASE_URL}/staff/${staffId}/schedules`,
      { headers: this.httpOptions.headers }).
      pipe(
        map((res: any) => {
          if (res) {
            return res.items;
          } else {
            return null;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  CreateStaff(BASE_URL: string, staff: CREATE_STAFF): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.POST_STAFF;
    return this.http.post(url, staff, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        if (res) {
          return res;
        }
        else {
          return null;
        }
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  UpdateStaff(BASE_URL: string, staffId: number, staff: UPDATE_STAFF): Observable<any> {
    const url = BASE_URL + CONST_URL_SEGEMENTS.STAFF.PUT_STAFF.replace('{staff_id}', staffId.toString());
    return this.http.put(url, staff, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        if (res) {
          return res;
        }
        else {
          return null;
        }
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  RequestAccess(requestData: AccessRequest, tenantId: number): Observable<Response> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.STAFF.REQUEST_ACCESS
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .post(url, requestData, { headers: this.httpOptions.headers })
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  StaffList(allParams: StaffListQueryParam[] = []): Observable<any> {
    let parsedParams = new HttpParams();
    parsedParams = this.appendQueryParams(parsedParams, allParams);
    return this.http.get(environment.BASE_URL + CONST_URL_SEGEMENTS.STAFF.GET_STAFF_LIST, {
      headers: this.httpOptions.headers, params: parsedParams
    }).pipe(map((res: any) => {
      if (res) {
        return res;
      }
      else {
        return null;
      }
    }), catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      }
      return throwError(() => new AppErrors(error));
    }));
  }

  private appendQueryParams(parsedParams: HttpParams, allParams: StaffListQueryParam[]): HttpParams {
    if (allParams.length > 0) {
      allParams.forEach((p) => {
        if (Array.isArray(p.value)) {
          p.value.forEach((item) => {
            parsedParams = parsedParams.append(p.name, item);
          });
        } else {
          parsedParams = parsedParams.append(p.name, p.value as string | number | boolean);
        }
      });
    }
    return parsedParams;
  }

  StaffNotification(): Observable<any> {
    let url = environment.BASE_URL + CONST_URL_SEGEMENTS.STAFF.STAFF_NOTIFICATIONS;
    return this.http.get(url, { headers: this.httpOptions.headers }).pipe(map((res: any) => {
      if (res) { return res; } else { return null; }
    }), catchError((error: Response) => {
      if (error.status === 404)
        return throwError(() => new NotFoundError(error));

      return throwError(() => new AppErrors(error));
    }));
  }

}
